import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../utils/constant';

const initialState = {
  materials: [],
  loading: false,
  error: null,
};

const BASE_URL = `${API_URL}/dispense-materials`;

// Fetch all dispense materials for a company
export const fetchDispenseMaterials = createAsyncThunk(
  'dispenseMaterial/fetchDispenseMaterials',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch materials');
    }
  }
);

// Seed dispense materials for a company
export const seedDispenseMaterials = createAsyncThunk(
  'dispenseMaterial/seedDispenseMaterials',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/seed/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to seed materials');
    }
  }
);

// Create a new dispense material
export const createDispenseMaterial = createAsyncThunk(
  'dispenseMaterial/createDispenseMaterial',
  async ({ companyId, materialData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/${companyId}`, materialData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create material');
    }
  }
);

// Update an existing dispense material
export const updateDispenseMaterial = createAsyncThunk(
  'dispenseMaterial/updateDispenseMaterial',
  async ({ id, materialData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/${id}`, materialData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update material');
    }
  }
);

// Delete a dispense material
export const deleteDispenseMaterial = createAsyncThunk(
  'dispenseMaterial/deleteDispenseMaterial',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete material');
    }
  }
);

const dispenseMaterialSlice = createSlice({
  name: 'dispenseMaterial',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchDispenseMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDispenseMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.materials = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchDispenseMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Seed
      .addCase(seedDispenseMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(seedDispenseMaterials.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(seedDispenseMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createDispenseMaterial.fulfilled, (state, action) => {
        state.materials.push(action.payload);
      })
      // Update
      .addCase(updateDispenseMaterial.fulfilled, (state, action) => {
        const index = state.materials.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.materials[index] = action.payload;
        }
      })
      // Delete
      .addCase(deleteDispenseMaterial.fulfilled, (state, action) => {
        state.materials = state.materials.filter(item => item.id !== action.payload);
      });
  },
});

export default dispenseMaterialSlice.reducer;
